// Shared config for the Mari Repeatability dev console.
// Single source of truth for the surface that launches runs (RunExperiment).
//
// NOTE: model IDs are work-in-progress — they'll be pinned to the final study set later.
// Keep them in sync with the server's model config when they change.

// Emulator provider → model id. Deliberately the cheapest / minimal-reasoning tier: the emulator just
// role-plays short patient replies, so a fast low-reasoning model is sufficient AND more consistent
// (better for a repeatability study). Reasoning is minimized server-side (emulator reasoning_effort="none":
// gpt-5.6-luna → reasoning.effort=none; claude-haiku-4-5 → thinking off; gemini-3.5-flash-lite → runs at its
// default, which emits ~no thinking tokens; Grok 4.3 runs with reasoning.effort=none).
// xai (Grok) is a 4th, SUT-decoupled family — its provider matches no SUT, so it never triggers
// anti-coupling and can serve as a single constant emulator across every SUT.
export const EMU = {
  xai: "grok-4.3",   // default emulator; listed first (Object.keys order drives the picker)
  anthropic: "claude-haiku-4-5",
  openai: "gpt-5.6-luna",
  google: "gemini-3.5-flash-lite",
};

// Study model sets (Run/Experiment "Model set" selector). "previous" = the frozen pre-2026-10-02 set;
// only the xai emulator differs client-side (the history-SUT swaps happen server-side — see the
// server's model-sets memory). Mari internals + the evaluator always run current (option B).
export const EMU_BY_SET = {
  current: EMU,
  previous: { ...EMU, xai: "grok-4-1-fast-non-reasoning" },
};
export const MODEL_SET_OPTIONS = [
  { id: "current", label: "Current" },
  { id: "previous", label: "Previous (pre-GPT-6 refresh)" },
];

// Systems under test the console can drive.
export const SYSTEMS = ["mari", "history:anthropic", "history:openai", "history:google"];

// Patient personas (redesigned 2026-08-09: population-representative bands, replacing the old 5 comm
// styles). Ids are what the server stores in comm_style / the `persona:<id>` prompt kind.
export const PERSONAS = ["baseline", "lower", "upper"];
export const PERSONA_LABELS = { baseline: "Baseline (avg adult)", lower: "Lower band", upper: "Upper band" };
export const personaLabel = (id) => PERSONA_LABELS[id] || id;

// Client-side APPROXIMATION of each system's internal model family, used only for the fast
// pre-launch anti-coupling hint. The server's /manifest is authoritative and is the real
// enforcement point at launch — e.g. Mari's internal family can change server-side without this
// map knowing. Prefer server families (see couplesWith) whenever you have them.
export const FAMILY_OF = {
  mari: "openai",
  "history:anthropic": "anthropic",
  "history:openai": "openai",
  "history:google": "google",
};

// True if the emulator provider shares a family with the system (i.e. the run would be coupled).
// Pass `serverFamilies` (from GET /api/systems/{system}/families) when available — it's authoritative;
// otherwise this falls back to the local FAMILY_OF approximation.
export function couplesWith(system, emuProvider, serverFamilies) {
  const families =
    serverFamilies && serverFamilies.length ? serverFamilies : [FAMILY_OF[system]].filter(Boolean);
  return families.includes(emuProvider);
}
